import { useEffect, useRef } from 'react'

interface UseUploadDialogSyncArgs {
  open: boolean
  /** The parent's latest delivery — see the contract below. */
  files: File[] | undefined
  /** Seed the form and clear last time's leftovers. Called once per open. */
  onOpen: () => void
  /** Add a delivery that arrived while the dialog was already open. */
  onDeliver: (files: File[]) => void
}

/**
 * Syncs an upload dialog with the files its parent hands it.
 *
 * The contract every upload dialog and every parent depends on: `files` is a
 * delivery, not the dialog's list. Opening seeds the form from it; each later
 * array identity is appended exactly once, because a full-screen drop has to
 * reach a dialog that is already open. The dialog owns the list from then on —
 * the user can remove chips from it and the parent never hears about that.
 *
 * So a parent must send a fresh array per drop and must never accumulate its
 * own copy: accumulating re-delivers files the user already removed, and they
 * reappear on the next drop.
 *
 * For the same reason a dialog clears itself in `onOpen` and never on close.
 * Radix keeps the content mounted through the exit animation, so resetting to
 * `files` on the way out plays the last delivery back at the user — including
 * the files they had just removed.
 */
export const useUploadDialogSync = ({
  open,
  files,
  onOpen,
  onDeliver,
}: UseUploadDialogSyncArgs) => {
  const wasOpenRef = useRef(open)
  const previousFilesRef = useRef(files)

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current
    wasOpenRef.current = open

    const delivered = files === previousFilesRef.current ? undefined : files
    previousFilesRef.current = files

    if (!open) return

    if (justOpened) {
      onOpen()
    } else if (delivered && delivered.length > 0) {
      onDeliver(delivered)
    }
  }, [open, files, onOpen, onDeliver])
}
